import { ApiErrorKeys } from 'constants/apiErrorKeys';

/** Laravel default validation summary (EN) — prefer field errors when this is all we have. */
const GENERIC_VALIDATION_MESSAGES = new Set(['The given data was invalid.', 'validation.failed']);

/** Preferred field order when flattening validation errors. */
const PREFERRED_ERROR_FIELDS = ['email', 'phone_number', 'role_ids', 'name', 'group_ids'];

/** Default Japanese messages for known API error_key values. */
export const DEFAULT_ERROR_KEY_MESSAGES = {
    [ApiErrorKeys.USER_INVITE_LIMIT]:
        'ユーザーの招待上限数を超えたため、ユーザーの追加に失敗しました',
    [ApiErrorKeys.ADMIN_INVITE_LIMIT]: '管理者の招待上限数を超えたため、管理者の追加に失敗しました',
    [ApiErrorKeys.USER_SEND_PASSWORD_FAILED]: 'パスワードの送信に失敗しました',
    [ApiErrorKeys.ADMIN_SEND_PASSWORD_FAILED]: 'パスワードの送信に失敗しました',
    [ApiErrorKeys.REPORT_CATEGORY_SUSPENDED]: '内部通報は現在停止中のため、送信できません。',
    [ApiErrorKeys.REPORT_CATEGORY_NOT_FOUND]:
        '内部通報カテゴリが見つかりません。管理者にお問い合わせください。',
};

/**
 * Flatten Laravel validation errors object into a list of strings.
 *
 * @param {object} errors
 * @returns {string[]}
 */
function flattenValidationErrors(errors) {
    if (!errors || typeof errors !== 'object') {
        return [];
    }

    const messages = [];

    PREFERRED_ERROR_FIELDS.forEach(field => {
        if (errors[field] != null) {
            const value = errors[field];
            if (Array.isArray(value)) {
                value.forEach(msg => messages.push(String(msg)));
            } else {
                messages.push(String(value));
            }
        }
    });

    Object.keys(errors).forEach(field => {
        if (PREFERRED_ERROR_FIELDS.includes(field)) {
            return;
        }
        const value = errors[field];
        if (Array.isArray(value)) {
            value.forEach(msg => messages.push(String(msg)));
        } else if (value != null) {
            messages.push(String(value));
        }
    });

    return messages;
}

/**
 * Resolve a user-facing error message from an axios error response.
 *
 * @param {object} error Axios error (or similar with response.data)
 * @param {object} [options]
 * @param {string} [options.fallback] Message when nothing specific is found
 * @param {object} [options.errorKeyMessages] Override map for error_key → message
 * @returns {string}
 */
export function extractApiErrorMessage(error, options = {}) {
    const { fallback = 'リクエストに失敗しました。', errorKeyMessages = {} } = options;
    const keyMessages = { ...DEFAULT_ERROR_KEY_MESSAGES, ...errorKeyMessages };
    const data = error?.response?.data;

    if (!data || typeof data !== 'object') {
        return fallback;
    }

    const errorKey = data.error_key ?? null;
    if (errorKey && keyMessages[errorKey]) {
        return keyMessages[errorKey];
    }

    const validationMessages = flattenValidationErrors(data.errors);
    if (validationMessages.length > 0) {
        return validationMessages[0];
    }

    const topMessage = data.message;
    if (topMessage && typeof topMessage === 'string' && topMessage.trim() !== '') {
        if (!GENERIC_VALIDATION_MESSAGES.has(topMessage.trim())) {
            return topMessage;
        }
    }

    return fallback;
}
